class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.init();
    this.attachEventListeners();
  }

  // Moved all event listeners to a separate method for better organization
  attachEventListeners() {
    this.buttonClose = this.querySelector(".js-button-close");
    this.buttonClose.addEventListener("click", this.closeNewsletter.bind(this));

    if (this.form) {
      this.form.addEventListener("submit", this.onSubscribe.bind(this));
    }
  }

  init() {
    this.delay = this.dataset.delay * 1000;
    this.alreadyShown = localStorage.getItem("newsletter-popup-shown") === "true";
    this.form = this.querySelector(".js-form");
    this.ageVerificationPopup = document.querySelector("age-verification-popup");
    this.watchFirstInteraction();
  }

  // The timer alone no longer opens the popup: the visitor must also have
  // scrolled, tapped or pressed a key since the page loaded. Someone who has
  // not touched the page yet is still reading the first screen, and a lab run
  // (Lighthouse never interacts) would otherwise count the late popup as a
  // late screen change. Whichever comes last, the timer or the first
  // interaction, opens it.
  watchFirstInteraction() {
    this.interacted = false;
    this.timerDone = false;
    const events = ["scroll", "pointerdown", "touchstart", "keydown"];
    const onFirst = () => {
      if (this.interacted) return;
      this.interacted = true;
      events.forEach((name) => window.removeEventListener(name, onFirst));
      if (this.timerDone && !this.alreadyShown) this.showAfterCountryRedirect();
    };
    events.forEach((name) => window.addEventListener(name, onFirst, { passive: true }));
  }

  // The popup image is parked in a <template> by sections/newsletter-popup.liquid
  // so it costs nothing on page load — the popup is always in the DOM and only
  // hidden with opacity/visibility, so a live <img> would be fetched even when
  // the popup never opens. Clone it in just before showing. Below 750px CSS has
  // .newsletter-popup__media at display:none, so the image is never fetched at
  // all there; the computed-style check keeps that CSS the single source of
  // truth for the breakpoint.
  hydrateMedia() {
    const holder = this.querySelector("[data-newsletter-media]");
    if (!holder) return;
    const template = holder.querySelector("template[data-newsletter-media-template]");
    if (!template) return;
    if (getComputedStyle(holder).display === "none") return;
    holder.appendChild(template.content.cloneNode(true));
    template.remove();
  }

  show(opener) {
    this.hydrateMedia();
    super.show(opener);
  }

  closeNewsletter() {
    localStorage.setItem("newsletter-popup-shown", "true");
    this.hide();
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
      this.show();
      }
      return;
    }

    this.handlePopupVisibility();
  }

  handlePopupVisibility() {
    if (getCookie("age-verified") !== "true" && this.ageVerificationPopup) {
      this.handleAgeVerificationPopup();
    } else {
      this.showPopupWithDelay();
    }
  }

  handleAgeVerificationPopup() {
    let prevClassState = this.classList.contains("newsletter-popup-is-hidden");

    const observer = new MutationObserver(mutations => {
      const mutation = mutations[0];
      if (mutation.attributeName !== "class") return;

      const currentClassState = mutation.target.classList.contains("newsletter-popup-is-hidden");

      if (prevClassState !== currentClassState) {
        prevClassState = currentClassState;
        if (!currentClassState) {
          this.showPopupWithDelay();
        }
      }
    });

    observer.observe(this, { attributes: true });
  }

  showPopupWithDelay() {
    setTimeout(() => {
      if (this.alreadyShown) return;
      this.timerDone = true;
      if (this.interacted) this.showAfterCountryRedirect();
    }, this.delay);
  }

  // The country-redirect popup and this newsletter popup must never appear at
  // the same time. If the country popup is still pending (waiting to auto-show,
  // or currently open), hold off until it dispatches "country-redirect:resolved".
  showAfterCountryRedirect() {
    if (window.__countryRedirectPending) {
      document.addEventListener(
        "country-redirect:resolved",
        () => this.showNow(),
        { once: true }
      );
    } else {
      this.showNow();
    }
  }

  showNow() {
    if (this.alreadyShown) return;
    this.alreadyShown = true;
    this.show();
    localStorage.setItem("newsletter-popup-shown", "true");
  }

  onSubscribe() {
    localStorage.setItem("newsletter-popup-shown", "true");
    this.hide();
  }
}

customElements.define("newsletter-popup", NewsletterPopup);
